import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Modal, Spinner } from 'react-bootstrap'
import { Trans, useTranslation } from 'react-i18next'

import Badge from '../badge'
import CategoryPill from '../category-pill'
import Icon from '../icon'
import PledgeAllocationCard from '../PledgeAllocationCard/PledgeAllocationCard'
import SponsorAdMessage from '../SponsorAdMessage/SponsorAdMessage'
import { formatAmount } from '../../lib/numbers'
import { pledgeAllocation } from '../../lib/pledges'
import styles from './PledgeResponseModal.module.scss'

/**
 * Organizer view of a sponsor pledge. Accept/decline only report the note;
 * the host performs the API call and closes the modal.
 */
export default function PledgeResponseModal({
  show,
  pledge,
  business,
  locale,
  submitting,
  onAccept,
  onDecline,
  onClose,
}) {
  const { t } = useTranslation()
  const [note, setNote] = useState('')

  useEffect(() => {
    if (show) setNote('')
  }, [show, pledge?.id])

  const { total, matchAmount, volunteerAmount } = pledgeAllocation(pledge)
  const businessName = business?.name
  const isPending = pledge?.state === 'pending'
  const categories = business?.categories ?? []
  const payload = () => ({ note: note.trim() })

  return (
    <Modal show={show} onHide={onClose} centered scrollable>
      <Modal.Header closeButton className={styles.header}>
        <Modal.Title className={styles.title}>
          <Icon name="hand-holding-dollar" className={styles.titleIcon} />
          {t('organizer.sponsorPledge.modal.heading')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.body}>
        <div>
          <Badge className="badge-beta-light" icon="flask" />
        </div>

        <p className={styles.summary}>
          <Trans
            i18nKey="organizer.sponsorPledge.modal.message"
            values={{ businessName, amount: formatAmount(total) }}
          />
        </p>

        <PledgeAllocationCard
          total={total}
          matchAmount={matchAmount}
          volunteerAmount={volunteerAmount}
        />

        {pledge?.message_to_event_owner ? (
          <p className={styles.sponsorMessage}>
            <strong>{t('organizer.sponsorPledge.modal.sponsorMessage')}</strong>{' '}
            {pledge.message_to_event_owner}
          </p>
        ) : null}

        {categories.length ? (
          <div>
            <p className={styles.sectionLabel}>
              {t('organizer.sponsorPledge.modal.supportCauses', {
                businessName,
              })}
            </p>
            <div className={styles.causes}>
              {categories.map(category => (
                <CategoryPill
                  key={category.id}
                  category={category}
                  locale={locale}
                  variant="cause"
                />
              ))}
            </div>
          </div>
        ) : null}

        <hr className={styles.divider} />

        <div>
          <p className={styles.sectionLabel}>
            {t('organizer.sponsorPledge.modal.preview')}
          </p>
          <SponsorAdMessage business={business} variant="champion" />
        </div>

        {isPending ? (
          <>
            <hr className={styles.divider} />
            <div>
              <label
                htmlFor="pledgeResponseNote"
                className={styles.sectionLabel}
              >
                {t('organizer.sponsorPledge.modal.sendMessage', {
                  businessName,
                })}
              </label>
              <textarea
                id="pledgeResponseNote"
                rows="3"
                className="form-control"
                placeholder={t('organizer.sponsorPledge.modal.notePlaceholder')}
                value={note}
                disabled={submitting}
                onChange={e => setNote(e.target.value)}
              />
            </div>
            <p className={styles.terms}>
              {t('organizer.sponsorPledge.modal.terms.message')}
              <a
                href={t('organizer.sponsorPledge.modal.terms.link.url')}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('organizer.sponsorPledge.modal.terms.link.text')}
              </a>
            </p>
          </>
        ) : null}
      </Modal.Body>
      {isPending ? (
        <Modal.Footer className={styles.footer}>
          <Button
            variant="light"
            className={styles.declineButton}
            disabled={submitting}
            onClick={() => onDecline(payload())}
          >
            {t('organizer.sponsorPledge.modal.declineButton')}
          </Button>
          <Button
            variant="primary"
            className={styles.acceptButton}
            disabled={submitting}
            onClick={() => onAccept(payload())}
          >
            {submitting ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <Icon name="check" />
            )}
            {t('organizer.sponsorPledge.modal.acceptButton')}
          </Button>
        </Modal.Footer>
      ) : null}
    </Modal>
  )
}

PledgeResponseModal.propTypes = {
  show: PropTypes.bool.isRequired,
  pledge: PropTypes.object,
  business: PropTypes.object,
  locale: PropTypes.string.isRequired,
  submitting: PropTypes.bool,
  onAccept: PropTypes.func.isRequired,
  onDecline: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
}
